/**
 * 模块编辑器（对应 iOS PDFReaderEditor / VideoPlayerEditor / HandwritingEditor）：
 * 选文件并上传、设置规则，"下一步"把 payload 交给上传流程填写标题等信息。
 */
import { CircleCheck, FileText, Film, Image as ImageIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type Asset, didWrite, type JSONObject, upload } from "../../core";
import { Button, C, FormCard, ModalHeader, NumberStepper, PillSegment, RowDivider, SectionLabel, SheetBody, Switch, TextField, ValueRow } from "../../ui";
import { type HanziEntry, handwritingPayload, mergePayload, uniqueHanzi } from "./activity";
import { HanziGlyph, strokeNames, useHanziData } from "./hanzi";
import { localPdfPageCount, rememberPageCount } from "./pdf";

// ---------- 上传状态（上传中弹窗不能关闭） ----------

export interface Uploader {
  uploading: boolean;
  stage: string;
  progress: number;
  /** 最近一次上传的文件名（生成默认标题用） */
  lastFileName: string | null;
  uploadFile: (file: File) => Promise<Asset>;
}

export function useUploader(): Uploader {
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState("");
  const [progress, setProgress] = useState(0);
  const [lastFileName, setLastFileName] = useState<string | null>(null);
  const uploadFile = async (file: File) => {
    setUploading(true);
    setProgress(0);
    setStage("正在准备文件…");
    try {
      const asset = await upload(file, { onProgress: setProgress, onStage: setStage });
      didWrite("assets", asset);
      setLastFileName(file.name);
      return asset;
    } finally {
      setUploading(false);
    }
  };
  return { uploading, stage, progress, lastFileName, uploadFile };
}

/** 编辑器给下一步（标题等信息）的默认值 */
export interface EditorMeta {
  title?: string;
  minutes?: number;
}

export interface EditorProps {
  uploader: Uploader;
  onCancel: () => void;
  onSave: (payload: JSONObject, version: number, meta?: EditorMeta) => void;
}

function errorText(e: unknown): string {
  return `上传失败：${e instanceof Error ? e.message : String(e)}`;
}

/** 隐藏的文件选择框 + 按钮 */
function FilePick({ accept, label, disabled, onFile, testId }: { accept: string; label: string; disabled?: boolean; onFile: (f: File) => void; testId: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        data-testid={testId}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onFile(f);
        }}
      />
      <Button variant="dark" height={44} disabled={disabled} onClick={() => input.current?.click()}>
        {label}
      </Button>
    </>
  );
}

function FileStatus({ icon: Icon, done, title, sub }: { icon: typeof FileText; done: boolean; title: string; sub?: string | null }) {
  return (
    <div className="learn-file-status">
      <span style={{ color: done ? C.success : C.inkMuted }}>{done ? <CircleCheck size={22} strokeWidth={2.4} /> : <Icon size={22} strokeWidth={2.2} />}</span>
      <div>
        <strong>{title}</strong>
        {sub && <small>{sub}</small>}
      </div>
    </div>
  );
}

// ---------- PDF ----------

export function PdfEditor({ uploader, onCancel, onSave }: EditorProps) {
  const [assetId, setAssetId] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [limitPages, setLimitPages] = useState(false);
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  const [byMinutes, setByMinutes] = useState(false);
  const [minMinutes, setMinMinutes] = useState(10);
  const [allowAnnotation, setAllowAnnotation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const maxPage = pageCount ?? 999;

  const pick = async (f: File) => {
    setError(null);
    if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
      setError("请选择 PDF 文件");
      return;
    }
    const n = await localPdfPageCount(f);
    if (n == null) {
      setError("这个文件不是有效的 PDF");
      return;
    }
    try {
      const a = await uploader.uploadFile(f);
      rememberPageCount(a.id, n);
      setAssetId(a.id);
      setFileName(f.name);
      setPageCount(n);
      setEndPage(n);
      setStartPage((s) => Math.min(s, n));
    } catch (e) {
      setError(errorText(e));
    }
  };

  const save = () => {
    if (!assetId) return;
    onSave(
      mergePayload(undefined, {
        pdf_asset_id: assetId,
        page_range: limitPages ? [startPage, Math.max(endPage, startPage)] : null,
        complete_when: byMinutes ? "min_minutes" : "last_page",
        min_minutes: byMinutes ? minMinutes : null,
        allow_annotation: allowAnnotation,
      }),
      1,
    );
  };

  return (
    <>
      <ModalHeader title="PDF 阅读" cancelTitle="返回" actionTitle="下一步" actionEnabled={!!assetId && !uploader.uploading} onCancel={onCancel} onAction={save} />
      <SheetBody>
        <SectionLabel>文件</SectionLabel>
        <FormCard>
          <div className="learn-file-box">
            <FileStatus icon={FileText} done={!!assetId} title={assetId ? "已上传 PDF" : "还没有选择 PDF"} sub={fileName ? `${fileName}${pageCount ? ` · 共 ${pageCount} 页` : ""}` : null} />
            <FilePick accept="application/pdf,.pdf" label={assetId ? "重新选择 PDF" : "选择 PDF 并上传"} disabled={uploader.uploading} onFile={(f) => void pick(f)} testId="pdf-file" />
          </div>
          {error && <p className="learn-error">{error}</p>}
        </FormCard>

        <SectionLabel>页码范围</SectionLabel>
        <FormCard>
          <Switch label="只读其中几页" checked={limitPages} onChange={setLimitPages} />
          {limitPages && (
            <>
              <RowDivider />
              <ValueRow title="从第几页" height={56}>
                <NumberStepper value={startPage} min={1} max={maxPage} onChange={(v) => {
                  setStartPage(v);
                  if (endPage < v) setEndPage(v);
                }} />
              </ValueRow>
              <RowDivider />
              <ValueRow title="到第几页" height={56}>
                <NumberStepper value={Math.max(endPage, startPage)} min={startPage} max={Math.max(startPage, maxPage)} onChange={setEndPage} />
              </ValueRow>
            </>
          )}
        </FormCard>
        <p className="learn-note">不限制时读整本 PDF。超出 PDF 实际页数的部分会自动忽略。</p>

        <SectionLabel>完成条件</SectionLabel>
        <FormCard>
          <div className="learn-card-pad">
            <PillSegment items={[[0, "读到最后一页"], [1, "读满时间"]] as const} value={byMinutes ? 1 : 0} onChange={(v) => setByMinutes(v === 1)} stretch background={C.bg} />
          </div>
          {byMinutes && (
            <>
              <RowDivider />
              <ValueRow title="至少读几分钟" height={56}>
                <NumberStepper value={minMinutes} min={1} max={120} onChange={setMinMinutes} />
              </ValueRow>
            </>
          )}
        </FormCard>

        <FormCard>
          <Switch label="允许画笔批注" checked={allowAnnotation} onChange={setAllowAnnotation} />
        </FormCard>
        <p className="learn-note">批注只保存在孩子的平板上，本次阅读结束后不保留。</p>
      </SheetBody>
    </>
  );
}

// ---------- 视频 ----------

export function VideoEditor({ uploader, onCancel, onSave }: EditorProps) {
  const [video, setVideo] = useState<{ id: string; name: string } | null>(null);
  const [poster, setPoster] = useState<{ id: string; name: string } | null>(null);
  const [allowSeek, setAllowSeek] = useState(true);
  const [percent, setPercent] = useState(90);
  const [error, setError] = useState<string | null>(null);

  const pick = async (f: File, kind: "video" | "poster") => {
    setError(null);
    const ok = kind === "video" ? f.type.startsWith("video/") || /\.(mp4|m4v|mov)$/i.test(f.name) : f.type.startsWith("image/");
    if (!ok) {
      setError(kind === "video" ? "请选择视频文件（建议 MP4）" : "请选择图片");
      return;
    }
    try {
      const a = await uploader.uploadFile(f);
      (kind === "video" ? setVideo : setPoster)({ id: a.id, name: f.name });
    } catch (e) {
      setError(errorText(e));
    }
  };

  const save = () => {
    if (!video) return;
    onSave(
      mergePayload(undefined, {
        video_asset_id: video.id,
        poster_asset_id: poster?.id ?? null,
        allow_seek: allowSeek,
        complete_percent: percent,
      }),
      1,
    );
  };

  return (
    <>
      <ModalHeader title="视频观看" cancelTitle="返回" actionTitle="下一步" actionEnabled={!!video && !uploader.uploading} onCancel={onCancel} onAction={save} />
      <SheetBody>
        <SectionLabel>视频</SectionLabel>
        <FormCard>
          <div className="learn-file-box">
            <FileStatus icon={Film} done={!!video} title={video ? "已上传视频" : "还没有选择视频"} sub={video?.name} />
            <FilePick accept="video/mp4,video/quicktime,video/*" label={video ? "重新选择视频" : "选择视频并上传"} disabled={uploader.uploading} onFile={(f) => void pick(f, "video")} testId="video-file" />
          </div>
        </FormCard>
        <p className="learn-note">建议使用 H.264 编码的 MP4。</p>

        <SectionLabel>封面（可选）</SectionLabel>
        <FormCard>
          <div className="learn-file-box">
            <FileStatus icon={ImageIcon} done={!!poster} title={poster ? "已上传封面" : "不设置封面"} sub={poster?.name} />
            <div className="learn-inline-actions">
              {poster && (
                <Button variant="ghost" height={44} className="learn-danger-text" onClick={() => setPoster(null)}>
                  去掉封面
                </Button>
              )}
              <FilePick accept="image/*" label={poster ? "重新选择封面" : "选择封面图片"} disabled={uploader.uploading} onFile={(f) => void pick(f, "poster")} testId="poster-file" />
            </div>
          </div>
        </FormCard>

        <SectionLabel>观看规则</SectionLabel>
        <FormCard>
          <Switch label="允许拖动进度条" checked={allowSeek} onChange={setAllowSeek} />
          <RowDivider />
          <ValueRow title={`看到 ${percent}% 算完成`} height={56}>
            <NumberStepper value={percent} min={50} max={100} step={5} onChange={setPercent} />
          </ValueRow>
        </FormCard>
        <p className="learn-note">拖动跳过的部分不计入观看进度。</p>
        {error && <p className="learn-error">{error}</p>}
      </SheetBody>
    </>
  );
}

// ---------- 写字 ----------

export function HandwritingEditor({ onCancel, onSave }: EditorProps) {
  const data = useHanziData();
  const [text, setText] = useState("");
  const [entries, setEntries] = useState<HanziEntry[]>([]);
  const [free, setFree] = useState(false);

  // 输入变化：去重，已有的条目保留家长改过的拼音 / 组词，新字用字表补全
  const rebuild = (t: string) => {
    setText(t);
    setEntries((old) =>
      uniqueHanzi(t).map((ch) => {
        const prev = old.find((e) => e.char === ch);
        if (prev) return prev;
        const lex = data?.lexicon[ch];
        return { char: ch, pinyin: lex?.pinyin ?? "", wordsText: (lex?.words ?? []).join("、") };
      }),
    );
  };

  // 字表晚于输入加载完成时，补上还空着的拼音 / 组词
  useEffect(() => {
    if (!data) return;
    setEntries((old) =>
      old.map((e) => {
        const lex = data.lexicon[e.char];
        if (!lex) return e;
        return { ...e, pinyin: e.pinyin || lex.pinyin, wordsText: e.wordsText || lex.words.join("、") };
      }),
    );
  }, [data]);

  const update = (i: number, patch: Partial<HanziEntry>) => setEntries((list) => list.map((e, j) => (j === i ? { ...e, ...patch } : e)));

  return (
    <>
      <ModalHeader title="写字练习" cancelTitle="返回" actionTitle="下一步" actionEnabled={entries.length > 0} onCancel={onCancel} onAction={() => onSave(handwritingPayload(entries, free), 1)} />
      <SheetBody>
        <SectionLabel>要练的字</SectionLabel>
        <input className="kp-field learn-hanzi-input" value={text} placeholder="例如：山石木禾田土" onChange={(e) => rebuild(e.target.value)} aria-label="要练的字" data-testid="hanzi-input" />
        <p className="learn-note">直接输入汉字，一次可以输入多个；重复的字只保留一个。</p>

        <SectionLabel>练习方式</SectionLabel>
        <PillSegment items={[[0, "描红"], [1, "自己写"]] as const} value={free ? 1 : 0} onChange={(v) => setFree(v === 1)} stretch />
        <p className="learn-note">{free ? "不显示浅色底字，孩子按笔顺自己写。" : "田字格里显示浅色底字，孩子照着描。"}</p>

        {entries.length > 0 && (
          <>
            <SectionLabel>拼音和组词</SectionLabel>
            <FormCard>
              {entries.map((e, i) => {
                const g = data?.graphics[e.char];
                return (
                  <div key={e.char}>
                    {i > 0 && <RowDivider />}
                    <div className="learn-hw-entry">
                      <div className="learn-hw-entry-glyph">
                        <span className="learn-tianzige is-small">
                          <HanziGlyph data={data} char={e.char} size={44} />
                        </span>
                        <small style={{ color: data && !g ? C.danger : C.inkSoft }}>{!data ? "…" : g ? `${g.strokes.length} 笔` : "无数据"}</small>
                      </div>
                      <div className="learn-hw-entry-fields">
                        <TextField value={e.pinyin} onChange={(v) => update(i, { pinyin: v })} placeholder="拼音" aria-label={`${e.char} 的拼音`} />
                        <TextField value={e.wordsText} onChange={(v) => update(i, { wordsText: v })} placeholder="组词，如：树木、木头" aria-label={`${e.char} 的组词`} />
                        {data && g && <small className="learn-strokes">{strokeNames(data, e.char).join(" ")}</small>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </FormCard>
            <p className="learn-note">组词之间用顿号、逗号或空格分开。没有笔顺数据的字只能自由书写，不判断笔画。</p>
          </>
        )}
      </SheetBody>
    </>
  );
}
